import React, {
  ChangeEventHandler,
  ComponentProps,
  memo,
  useCallback,
  useRef,
  useState,
} from 'react';
import {
  AutoComplete,
  AutoCompleteInput,
  AutoCompleteItem,
  AutoCompleteList,
  AutoCompleteProps,
} from '@choc-ui/chakra-autocomplete';
import { colors } from '../../../theme/colors';

import * as Styles from './styles';
import { InputGroup, InputLeftElement, InputRightElement } from '@chakra-ui/input';
import { Icon, Label, TIconName } from 'components';

interface IOption {
  [key: string]: any;
}

type TIconProp = ComponentProps<typeof Icon> | TIconName;

interface IProps extends Omit<AutoCompleteProps, 'children'> {
  options: IOption[];
  optionLabel?: string;
  optionValue?: string;
  placeholder?: string;
  emptyMessage?: string;
  label?: string;
  notFilter?: boolean;
  isInvalid?: boolean;
  disabled?: boolean;
  leftIcon?: TIconProp;
  inputRef?: React.RefObject<HTMLInputElement>;
  onInputChange?(value: string): void;
  maxHeightSuggestion?: number;
  defaultValue?: Record<string, any> | Record<string, any>[];
  notClearable?: boolean;
  action?: {
    name: string | JSX.Element;
    cb: () => void | Promise<void>;
  };
  name?: string;
}

const AutocompleteFieldComponent: React.FC<IProps> = ({
  options,
  notFilter,
  optionLabel = 'label',
  optionValue = 'value',
  placeholder = '',
  emptyMessage = 'Nenhuma opção encontrada',
  leftIcon,
  inputRef,
  onSelectOption,
  onInputChange,
  isInvalid,
  label,
  maxHeightSuggestion,
  defaultValue,
  disabled,
  notClearable,
  action,
  ...rest
}) => {
  const autoRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState('');

  const handleFilter = useCallback(
    (query: string = '', itemValue: string, itemLabel: string = '') => {
      if (notFilter) {
        return true;
      }

      if (itemLabel) {
        return itemLabel.toLowerCase().includes(query.toLowerCase());
      }
      return true;
    },
    [notFilter],
  );

  const handleChangeInput = useCallback<ChangeEventHandler<HTMLInputElement>>(
    e => {
      if (e) {
        onInputChange?.(e.target.value);
      } else {
        onInputChange?.('');
      }

      if (!e) {
        const selectedOption = options.find(option => option[optionValue] === selectedValue);

        if (selectedOption) {
          setInputValue(selectedOption[optionLabel]);
        } else {
          setInputValue('');
        }

        return '';
      }
      setInputValue(e.target.value);
    },
    [selectedValue, onInputChange, options],
  );

  const handleChangeOption = useCallback(
    (value: {
      optionValue: string;
      selectMethod: 'mouse' | 'keyboard' | null;
      isNewInput: boolean;
    }) => {
      onSelectOption?.(value);
      if (!action || !(value.optionValue === `action_${action.name}`))
        setSelectedValue(value.optionValue);
    },
    [onSelectOption],
  );

  return (
    <Styles.Container>
      {label && <Label htmlFor={label}>{label}</Label>}

      <AutoComplete
        {...rest}
        defaultValues={
          defaultValue
            ? Array.isArray(defaultValue)
              ? defaultValue
              : [defaultValue[optionLabel]]
            : undefined
        }
        filter={handleFilter}
        openOnFocus
        suggestWhenEmpty
        listAllValuesOnFocus
        emptyState={() => <Styles.EmptyItemContainer>{emptyMessage}</Styles.EmptyItemContainer>}
        freeSolo
        closeOnSelect
        closeOnBlur
        onSelectOption={handleChangeOption}
        ref={autoRef}
      >
        {({ isOpen, onOpen, onClose }) => (
          <>
            <InputGroup>
              {leftIcon && (
                <InputLeftElement>
                  <Icon {...(typeof leftIcon === 'string' ? { name: leftIcon } : leftIcon)} />
                </InputLeftElement>
              )}

              <AutoCompleteInput
                style={{ border: '1px solid #ACB4BA' }}
                ref={inputRef}
                placeholder={placeholder}
                wrapStyles={{ w: '100%' }}
                onChange={handleChangeInput}
                onBlur={handleChangeInput}
                value={inputValue}
                isInvalid={isInvalid}
                disabled={disabled}
              />

              <InputRightElement>
                <Styles.DropdownIconContainer
                  isOpen={isOpen}
                  onClick={() => !disabled && (isOpen ? onClose() : onOpen())}
                >
                  <Icon name="roundedArrowDown" />
                </Styles.DropdownIconContainer>
              </InputRightElement>
            </InputGroup>

            <AutoCompleteList
              bgColor="brand.1300"
              p={0}
              mt="1px"
              borderRadius="10px"
              maxBlockSize={maxHeightSuggestion && `${maxHeightSuggestion}rem`}
            >
              {!notClearable ? (
                <AutoCompleteItem
                  key="option-empty"
                  value=""
                  label=""
                  item
                  bg="brand.1300"
                  w="100%"
                  m="0"
                  borderRadius="0"
                  transition="filter .3s, background .3s"
                  __css={{
                    color: 'brand.700',
                    fontStyle: 'italic',
                  }}
                  _focus={{ filter: 'brightness(96%)' }}
                >
                  {placeholder}
                </AutoCompleteItem>
              ) : (
                <> </>
              )}

              {options.map((option, oid) => (
                <AutoCompleteItem
                  key={`option-${oid}`}
                  value={option[optionValue]}
                  label={option[optionLabel]}
                  item
                  bg="brand.1300"
                  w="100%"
                  m="0"
                  borderRadius="0"
                  onMouseDown={e => {
                    setSelectedValue('');
                    setTimeout(() => {
                      // @ts-ignore
                      e.target?.click();
                    }, 0);
                  }}
                  transition="filter .3s, background .3s"
                  _selected={{ bg: 'brand.1000' }}
                  _focus={{ filter: 'brightness(96%)' }}
                >
                  {option[optionLabel]}
                </AutoCompleteItem>
              ))}
              {!!action && !!action.name && !!action.cb ? (
                <AutoCompleteItem
                  key="action-option"
                  // value={`action_${action.name}`}
                  value=""
                  label=""
                  bg="brand.1300"
                  w="100%"
                  m="0"
                  borderRadius="0"
                  onClick={e => {
                    e.preventDefault();
                    if (action.cb) action.cb();
                    setTimeout(() => {
                      setSelectedValue('');
                      setInputValue('');
                    }, 500);
                  }}
                  transition="filter .3s, background .3s"
                  __css={{
                    fontWeight: '700',
                    color: `${colors.brand.tertiary}`,
                    borderColor: '#ACB4BA',
                  }}
                  _selected={{ bg: 'brand.1300 !important' }}
                  _focus={{ filter: 'brightness(100%)' }}
                >
                  {action.name}
                </AutoCompleteItem>
              ) : (
                <></>
              )}
            </AutoCompleteList>
          </>
        )}
      </AutoComplete>
    </Styles.Container>
  );
};

export const AutocompleteField = memo(AutocompleteFieldComponent);
